import { Text } from '@cellula/react';
import { variableToRgb } from '@modules/color';
import { rem } from '@visiba/std';
import { Children, ReactElement, ReactNode } from 'react';
import styled from 'styled-components';
import { theme } from '../application/services/theme.service/theme';
import { FONT_VARIANT, STANDARD_BOX_SHADOW } from '../constants';

interface Props {
	children: ReactNode;
}

const elements = {
	outerContainer: styled.div`
		width: 100%;
		background-color: white; /* TODO: Use color alias UICardBg (DEV-10636) */
		border-radius: ${rem(10)};
		overflow: hidden;
		box-shadow: ${STANDARD_BOX_SHADOW};

		--_gap: ${rem(20)};
	`,
	innerContainer: styled.div`
		display: flex;
		align-items: center;
		padding: ${rem(15)} 0;
	`,
	thumbnail: styled.div<{ size?: number; type?: 'neutral' | 'alertive' | 'themed' }>`
		padding: 0 var(--_gap);
		display: flex;
		${({ size }) => size && `font-size: ${rem(size)};`}
		${({ type }) => {
			if (type) {
				if (type === 'alertive') {
					return `color: ${theme.systemColorError}`;
				}

				if (type === 'themed') {
					return `color: ${theme.primary}`;
				}

				// TODO: Use color alias UICardSecondaryText (DEV-10636)
				return 'color: #4C515A';
			}

			return null;
		}}
	`,
	textContainer: styled.div`
		display: flex;
		flex-direction: column;
		flex: 1;
		overflow: hidden;
	`,
	iconWrapper: styled.div<{ useAccentColor: boolean }>`
		display: flex;
		align-items: center;
		box-sizing: content-box;
		padding: 0 calc(var(--_gap) / 2) 0 var(--_gap);
		width: ${rem(32)};
		color: ${({ useAccentColor }) => (useAccentColor ? theme.accentColor : '#4C515A')};
	`,
	icon: styled.div`
		display: flex;
		font-size: ${rem(32)};
	`,
	footer: styled.div`
		width: 100%;
		padding: ${rem(3)} var(--_gap) ${rem(5)};
		background: ${() => `rgba(${variableToRgb(theme.accentColor)}, 0.05)`};
		color: ${() => theme.accentColor};
	`,
	modifiedText: styled(Text)`
		text-overflow: ellipsis;
		overflow: hidden;
		white-space: nowrap;
	`,
};

export function CaseCard({ children }: Props): JSX.Element {
	const childrenByType = new Map(Children.toArray(children).map((child) => [(child as ReactElement)?.type, child]));
	const hasFooter = !!childrenByType.get(CaseCardFooter);

	return (
		<elements.outerContainer data-testid='caseCard'>
			<elements.innerContainer>
				{childrenByType.get(CaseCardThumbnail)}
				<elements.textContainer>
					{childrenByType.get(CaseCardTitle)}
					{childrenByType.get(CaseCardSubtitle)}
					{childrenByType.get(CaseCardDate)}
				</elements.textContainer>
				<elements.iconWrapper useAccentColor={hasFooter}>{childrenByType.get(CaseCardIcon)}</elements.iconWrapper>
			</elements.innerContainer>

			{hasFooter ? <elements.footer>{childrenByType.get(CaseCardFooter)}</elements.footer> : null}
		</elements.outerContainer>
	);
}

interface CaseCardImageImageWithIconProps {
	size: number;
	type: 'neutral' | 'alertive' | 'themed';
	children: ReactNode;
}

interface CaseCardImageImageWithAvatarProps {
	size?: never;
	type?: never;
	children: ReactNode;
}

type CaseCardImageImageProps = CaseCardImageImageWithIconProps | CaseCardImageImageWithAvatarProps;

function CaseCardThumbnail(props: CaseCardImageImageProps): JSX.Element {
	if (props.size) {
		return (
			<elements.thumbnail size={props.size} type={props.type}>
				{props.children}
			</elements.thumbnail>
		);
	}

	return <elements.thumbnail>{props.children}</elements.thumbnail>;
}
CaseCard.Thumbnail = CaseCardThumbnail;

interface CaseCardTitleProps {
	children: string;
}

function CaseCardTitle({ children }: CaseCardTitleProps): JSX.Element {
	return (
		/* TODO: Use color alias UICardText (DEV-10636) */
		<elements.modifiedText variant='bodyLargeRegular' color='#323232'>
			{children}
		</elements.modifiedText>
	);
}
CaseCard.Title = CaseCardTitle;

interface CaseCardSubtitleProps {
	children: ReactNode;
}

function CaseCardSubtitle({ children }: CaseCardSubtitleProps): JSX.Element {
	return (
		/* TODO: Use color alias UICardSecondaryText (DEV-10636) */
		<elements.modifiedText variant='bodyRegular' color='#161718'>
			{children}
		</elements.modifiedText>
	);
}
CaseCard.Subtitle = CaseCardSubtitle;

interface CaseCardDateProps {
	children: ReactNode;
}

function CaseCardDate({ children }: CaseCardDateProps): JSX.Element {
	return (
		/* TODO: Use color alias UICardText (DEV-10636) */
		<elements.modifiedText variant='bodyRegular' color='#323232'>
			{children}
		</elements.modifiedText>
	);
}
CaseCard.Date = CaseCardDate;

interface CaseCardIconProps {
	children: ReactNode;
}

function CaseCardIcon({ children }: CaseCardIconProps): JSX.Element {
	/* TODO: Use color alias as fallback UICardSecondaryText (DEV-10636) */
	return <elements.icon>{children}</elements.icon>;
}
CaseCard.Icon = CaseCardIcon;

interface CaseCardFooterProps {
	children: string;
}

function CaseCardFooter({ children }: CaseCardFooterProps): JSX.Element {
	return <elements.modifiedText variant='bodySemiBold'>{children}</elements.modifiedText>;
}
CaseCard.Footer = CaseCardFooter;
